import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle, ArrowRight, AlertTriangle, Award, BookOpen,
  Globe, Monitor, Users, Mic, Stethoscope, Heart, Brain, Laptop,
  Shield, FileText, Star
} from 'lucide-react';

const eligibleActivities = [
  { icon: Monitor, label: 'Online Courses' },
  { icon: Users, label: 'Conferences' },
  { icon: Globe, label: 'Congresses' },
  { icon: Stethoscope, label: 'Workshops' },
  { icon: Mic, label: 'Symposia' },
  { icon: Award, label: 'Fellowship Programs' },
  { icon: Laptop, label: 'Webinars' },
  { icon: BookOpen, label: 'Faculty Development Programs' },
  { icon: Heart, label: 'Clinical Skills Programs' },
  { icon: Shield, label: 'Healthcare Quality Programs' },
  { icon: Brain, label: 'Leadership Programs' },
  { icon: FileText, label: 'Digital Health Programs' },
];

const recognitionLevels = [
  {
    level: 1,
    title: 'IMCAC CPD Recognized',
    desc: 'Awarded following successful review of educational objectives and learning outcomes.',
    color: 'bg-blue-50 border-blue-200 text-blue-700',
    badge: 'bg-blue-100 text-blue-800',
  },
  {
    level: 2,
    title: 'IMCAC CPD Quality Assured',
    desc: 'Awarded following review of educational quality, learner assessment, and faculty oversight.',
    color: 'bg-[#C5A572]/10 border-[#C5A572]/30 text-[#C5A572]',
    badge: 'bg-[#C5A572]/20 text-[#9A7D4E]',
  },
  {
    level: 3,
    title: 'IMCAC Advanced CPD Recognized',
    desc: 'Awarded to programs demonstrating strong competency-based learning design and measurable educational outcomes.',
    color: 'bg-[#1B2A4A]/5 border-[#1B2A4A]/20 text-[#1B2A4A]',
    badge: 'bg-[#1B2A4A] text-white',
  },
];

const reviewCriteria = [
  'Learning Objectives', 'Curriculum Structure', 'Educational Design',
  'Faculty Qualifications', 'Assessment Methods', 'Learning Outcomes',
  'Participant Evaluation', 'Educational Hours', 'Scientific References',
  'Evidence-Based Content', 'Quality Assurance Processes',
];

const applicationSteps = [
  { step: 1, title: 'Submit Application', desc: 'Complete and submit the CPD Recognition application form with required documentation.' },
  { step: 2, title: 'Document Review', desc: 'IMCAC reviews submitted documentation for completeness and eligibility.' },
  { step: 3, title: 'Educational Assessment', desc: 'Educational content, design, and quality are evaluated against IMCAC standards.' },
  { step: 4, title: 'CPD Credit Allocation', desc: 'CPD credit values are determined based on educational hours and methodology.' },
  { step: 5, title: 'Recognition Decision', desc: 'The recognition level is determined and communicated to the applicant.' },
  { step: 6, title: 'Recognition Certificate Issued', desc: 'A formal CPD Recognition certificate is issued upon successful review.' },
];

export default function CPDRecognitionOverviewPage() {
  return (
    <div data-testid="cpd-recognition-overview-page">
      {/* Hero */}
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">CPD Recognition</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            IMCAC Continuing Professional Development (CPD) Recognition
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            IMCAC evaluates educational activities delivered by independent organizations and may assign CPD credit values based on educational quality, learning outcomes, and participant engagement.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-5">
        <div className="imcac-container">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              IMCAC CPD Recognition evaluates educational activities for Continuing Professional Development purposes only. Recognition does not constitute governmental approval, university accreditation, specialist recognition, professional licensure, degree granting authority, or regulatory endorsement.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="imcac-section bg-white">
        <div className="imcac-container max-w-4xl">
          <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-4">Overview</h2>
          <p className="text-base text-slate-600 leading-relaxed mb-4">
            IMCAC offers Continuing Professional Development (CPD) Recognition for educational activities delivered by independent organizations, institutions, healthcare providers, professional societies, universities, academies, and training organizations.
          </p>
          <p className="text-base text-slate-600 leading-relaxed mb-4">
            The purpose of CPD Recognition is to evaluate educational quality and assign CPD credit values that may support ongoing professional development and certification maintenance.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
            <p className="text-sm text-slate-700 font-medium">
              IMCAC does not provide training or educational programs directly. CPD Recognition is an evaluation service for activities delivered by independent third-party organizations.
            </p>
          </div>
        </div>
      </section>

      {/* Eligible Activities */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="text-center mb-10">
            <p className="imcac-overline mb-3">What Can Be Submitted</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1B2A4A] font-heading">Eligible Activities</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {eligibleActivities.map((activity, i) => (
              <Card key={i} className="imcac-card text-center" data-testid={`eligible-activity-${i}`}>
                <CardContent className="p-4">
                  <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center mx-auto mb-3">
                    <activity.icon className="h-5 w-5 text-[#C5A572]" />
                  </div>
                  <p className="text-xs font-medium text-[#1B2A4A]">{activity.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Recognition Levels */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="text-center mb-10">
            <p className="imcac-overline mb-3">Recognition Framework</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1B2A4A] font-heading">CPD Recognition Levels</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recognitionLevels.map((level) => (
              <Card key={level.level} className={`border-t-4 ${level.color}`} data-testid={`recognition-level-${level.level}`}>
                <CardContent className="p-6">
                  <Badge className={`${level.badge} mb-3`}>Level {level.level}</Badge>
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-2">{level.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{level.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Review Criteria */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-4xl">
          <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-6">Review Criteria</h2>
          <p className="text-base text-slate-600 mb-6">IMCAC may evaluate the following aspects of submitted educational activities:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {reviewCriteria.map((criteria, i) => (
              <div key={i} className="flex items-center gap-3 bg-white p-3 rounded-sm border border-slate-200">
                <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0" />
                <span className="text-sm text-slate-700">{criteria}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CPD Credit Framework */}
      <section className="imcac-section bg-white">
        <div className="imcac-container max-w-4xl">
          <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-6">CPD Credit Framework</h2>
          <Card className="imcac-card border-l-4 border-l-[#C5A572] mb-6">
            <CardContent className="p-6">
              <p className="text-lg font-semibold text-[#1B2A4A] mb-2">1 Hour Verified Learning = 1 CPD Credit</p>
              <p className="text-sm text-slate-600">Credits are assigned following review and may vary based on educational design and assessment methodology.</p>
            </CardContent>
          </Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cpd-credit-table">
              <thead>
                <tr className="bg-[#1B2A4A] text-white">
                  <th className="p-3 text-left font-medium">Learning Hours</th>
                  <th className="p-3 text-left font-medium">CPD Credits</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { hours: '10 Learning Hours', credits: '10 CPD Credits' },
                  { hours: '20 Learning Hours', credits: '20 CPD Credits' },
                  { hours: '40 Learning Hours', credits: '40 CPD Credits' },
                  { hours: '80 Learning Hours', credits: '80 CPD Credits' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="p-3 text-slate-700">{row.hours}</td>
                    <td className="p-3 text-slate-700 font-medium">{row.credits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="text-center mb-10">
            <p className="imcac-overline mb-3">How It Works</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1B2A4A] font-heading">Application Process</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {applicationSteps.map((step) => (
              <Card key={step.step} className="imcac-card" data-testid={`app-step-${step.step}`}>
                <CardContent className="p-5">
                  <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center mb-3">
                    <span className="text-sm font-bold text-[#C5A572]">{step.step}</span>
                  </div>
                  <h3 className="text-base font-semibold text-[#1B2A4A] font-heading mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1B2A4A] py-12">
        <div className="imcac-container text-center">
          <h2 className="text-2xl font-bold text-white font-heading mb-4">Ready to Submit Your Educational Activity?</h2>
          <p className="text-sm text-slate-300 mb-6 max-w-xl mx-auto">
            Independent organizations may apply for IMCAC CPD Recognition of their educational activities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/cpd-recognition/apply" data-testid="cta-apply-btn">
              <Button className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm px-8">
                Apply for CPD Recognition <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/cpd-recognition/recognized-activities" data-testid="cta-directory-btn">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm px-8">
                View Recognized Activities
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
